/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ShieldCheck, KeyRound, History, BadgeCheck } from 'lucide-react';
import { AlboVerifyCard } from '@/components/AlboVerifyCard';
import { TwoFactorCard } from '@/components/TwoFactorCard';
import { ConsentCenter } from '@/components/ConsentCenter';
import { AuditLogViewer } from '@/components/AuditLogViewer';

/**
 * Trust & Security center — one-stop shop for:
 *  - Doctor: Albo verification + 2FA + Consents + Audit log
 *  - Patient: 2FA + Consents + Audit log (no Albo)
 */
const SecurityPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPro = user?.category !== 'paziente';

  return (
    <div className="space-y-5" data-testid="security-page">
      {/* Hero */}
      <div className="rounded-3xl bg-hero-gradient border border-border/60 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass text-[11px] font-semibold uppercase tracking-wider text-primary mb-3">
            <ShieldCheck className="h-3 w-3" /> {t('securityBadge')}
          </div>
          <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{t('securityTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">{t('securitySubtitle')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {isPro && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs"><BadgeCheck className="h-3 w-3 text-primary" /> {t('securityChipAlbo')}</span>}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs"><KeyRound className="h-3 w-3 text-primary" /> {t('securityChip2FA')}</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs"><ShieldCheck className="h-3 w-3 text-accent" /> {t('securityChipGDPR')}</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs"><History className="h-3 w-3" /> {t('securityChipAudit')}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {isPro && <AlboVerifyCard />}
        <TwoFactorCard />
        <ConsentCenter />
        <AuditLogViewer />
      </div>
    </div>
  );
};

export default SecurityPage;
